import { Col, Row } from 'antd';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BACKSPLASH_ID, CURRENCY_FORMAT, WATERFALL_ID } from '@common/constants';
import formatUOM from '@common/helpers/formatUOM';
import { getUnitType } from '@common/helpers/price-helper';
import { useShapeArea } from '@common/hooks/useShapeArea/useShapeArea';
import { PricedProduct, RetailerDocumentType } from '@common/models';
import { getProductDescription } from '@common/utils';
import CheckoutButton from '@components/buttons/checkout-button/checkout-button';
import TermsAndConditionsButton from '@components/buttons/terms-and-conditions-button/terms-and-conditions-button';
import PromotionDateLabel from '@components/labels/promotion-date-label/promotion-date-label';
import BasicInstallationPopOver from '@components/pop-overs/basic-installation-pop-over/basic-installation-pop-over';
import { Error } from '@core/components';
import useRetailerDocuments from '@core/hooks/useRetailerDocuments';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSmallJobs } from '@core/hooks/useSmallJobs';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { usePricedProductsContext } from '@ee-context';
import { useAppSelector } from '@redux/hooks';
import { selectIsSavingEstimate } from '@redux/slices/estimateSlice';

import { getPromotionEndDate } from '../order-summary/EstimateSection/estimate-section.utils';
import TermsAndConditionsModal from './components/TermsAndConditonsModal/TermsAndConditionsModal';
import PriceAreaDefaultInfo from './components/price-area-default-info';
import { SelectedColor } from './selected-color';

import './price.scss';

interface PriceProps {
    onCheckoutPressed: () => void;
}

interface PriceDetailedItemProps {
    priceItem: PricedProduct;
}

const Price = ({ onCheckoutPressed }: PriceProps): JSX.Element => {
    const { t } = useTranslation();

    const [isTnCModalVisible, setIsTnCModalVisible] = useState(false);
    const { retailerSettings, userOptions } = useRetailerSettings();
    const showUnitPrice = userOptions.showUnitPrice;
    const termsAndConditionsURL = useRetailerDocuments(RetailerDocumentType.PRIVACY_POLICY);

    const {
        priceTotals: { priceWithDiscount, priceWithoutDiscount },
        pricedProducts,
        error,
    } = usePricedProductsContext();
    const { useMinQtyAreaForPricing: showMinQtyWarning, smallJobMinimumArea } = useSmallJobs();
    const { selectedValue: selectedColor } = useSelectedColor();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();

    const { totalBaseCountertopArea } = useShapeArea();

    const isSavingEstimate = useAppSelector(selectIsSavingEstimate);

    const TotalPrice = () => {
        return (
            <div className="total-price-container">
                <div>{t('PRICING_AREA.TOTAL')}</div>
                <div className="total-price">
                    <span>
                        {CURRENCY_FORMAT.format(priceWithDiscount)}
                        {showMinQtyWarning ? '*' : ''}
                    </span>
                </div>
            </div>
        );
    };

    const PromotionPrice = (): JSX.Element => {
        const savings = priceWithoutDiscount - priceWithDiscount;

        return (
            <div className="total-price-container">
                <div className="promotion-save-container">
                    {t('PRICING_AREA.SAVE_PROMOTION')} {CURRENCY_FORMAT.format(savings)}
                </div>
                <div className="total-price-promotion">{CURRENCY_FORMAT.format(priceWithoutDiscount)}</div>
            </div>
        );
    };

    const PriceDetails = (): JSX.Element => {
        return (
            <>
                <div className="title">{t('PRICING_AREA.PRICE_DETAILS')}</div>
                <hr className="title-divider" />
                <div>
                    {pricedProducts.length
                        ? pricedProducts.map((pricedProduct) => (
                              <PriceDetailedItem key={pricedProduct.listKey || uniqueId()} priceItem={pricedProduct} />
                          ))
                        : null}
                </div>

                {showMinQtyWarning && selectedMaterial ? (
                    <div className="price-minimum-warning">
                        {t('PRICING_AREA.MINIMUM_PURCHASE_NOTE', { sqft: smallJobMinimumArea })}
                    </div>
                ) : null}
            </>
        );
    };

    const PriceAreaInfo = (props: { isInstalled: boolean }): JSX.Element => {
        if (error) {
            return (
                <div className="error-container">
                    <Error errorStyle="error-message" errorMessage={t('ERROR_MESSAGE.STH_WENT_WRONG')} />
                </div>
            );
        }

        const expirationPromotionDate = getPromotionEndDate(pricedProducts);

        return (
            <div className="main-price-container">
                <SelectedColor
                    material={selectedMaterial}
                    selectedColor={selectedColor}
                    countertopAreaQty={totalBaseCountertopArea}
                />
                <TotalPrice />
                {expirationPromotionDate ? (
                    <>
                        <PromotionPrice />
                        <PromotionDateLabel expirationDate={expirationPromotionDate} />
                    </>
                ) : null}
                {props.isInstalled ? <BasicInstallationPopOver /> : null}
                <CheckoutButton onCheckoutPressed={onCheckoutPressed} disabled={isSavingEstimate} />

                {retailerSettings?.showPriceDetails?.showDetails ? <PriceDetails /> : null}

                <TermsAndConditionsButton
                    onClick={() => setIsTnCModalVisible(true)}
                    label={'PRICING_AREA.TERMS_AND_CONDITIONS'}
                />
            </div>
        );
    };

    const PriceDetailedItem = (props: PriceDetailedItemProps) => {
        const { priceItem } = props;
        const quantity = priceItem.quantity.toFixed(0);
        const showLineItemQtyWarning =
            showMinQtyWarning &&
            (priceItem.name === selectedColor?.name ||
                priceItem.lineItemId === BACKSPLASH_ID ||
                priceItem.lineItemId === WATERFALL_ID);

        const unitOfMeasure = formatUOM(getUnitType(priceItem));

        const descriptionColumnWidth = showUnitPrice ? 9 : 12;
        const otherColumnsWidth = showUnitPrice ? 5 : 6;
        const priceIsDiscounted =
            !!priceItem.unitPriceWithoutDiscount && (priceItem?.unitPrice ?? 0) !== priceItem.unitPriceWithoutDiscount; // original price must have a value, then null==0 test

        return (
            <Row className="price-item-container" justify="space-around" align="middle" gutter={8} wrap>
                <Col span={descriptionColumnWidth}>
                    <div className="product-name">{getProductDescription(priceItem)}</div>
                </Col>
                <Col span={otherColumnsWidth}>
                    <div className="product-qty">
                        <span className={classNames({ 'minimum-warning': showLineItemQtyWarning })}>
                            {quantity} {unitOfMeasure} {showLineItemQtyWarning ? '*' : ''}
                        </span>
                    </div>
                </Col>
                {showUnitPrice && (
                    <Col span={otherColumnsWidth}>
                        <div className="product-price">
                            <span className={classNames({ 'discounted-price': priceIsDiscounted })}>
                                {CURRENCY_FORMAT.format(Number(priceItem.unitPrice))}
                            </span>
                            {priceIsDiscounted ? (
                                <>
                                    <br />
                                    <span className={'original-price'}>
                                        {CURRENCY_FORMAT.format(Number(priceItem.unitPriceWithoutDiscount))}
                                    </span>
                                </>
                            ) : null}
                        </div>
                    </Col>
                )}

                <Col span={otherColumnsWidth}>
                    <div className="product-price">
                        {/*{CURRENCY_FORMAT.format(Number(priceItem.priceWithoutDiscount))}*/}
                        <span className={classNames({ 'discounted-price': priceIsDiscounted })}>
                            {CURRENCY_FORMAT.format(Number(priceItem.price))}
                        </span>
                        {priceIsDiscounted ? (
                            <>
                                <br />
                                <span className={'original-price'}>
                                    {CURRENCY_FORMAT.format(Number(priceItem.priceWithoutDiscount))}
                                </span>
                            </>
                        ) : null}
                    </div>
                </Col>
            </Row>
        );
    };

    return (
        <div className="price-container">
            {pricedProducts.length && selectedColor ? (
                <PriceAreaInfo isInstalled={selectedMaterial?.isInstalled ?? true} />
            ) : (
                <PriceAreaDefaultInfo selectedColor={selectedColor} />
            )}
            <TermsAndConditionsModal
                title={t('PRICING_AREA.TERMS_AND_CONDITIONS')}
                documentURL={termsAndConditionsURL ?? ''}
                visible={isTnCModalVisible}
                onCancel={() => setIsTnCModalVisible(false)}
            />
        </div>
    );
};

export default Price;
